import { useEffect, useRef, useState } from "react";
import { Flame, Send, Share2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { toast } from "sonner";

function DragonMessageBubble({ msg, dragonAvatar, onShareDecision }) {
  const isDragon = msg.role === "assistant";
  const pendingShare = isDragon && msg.shareable && !msg.shared && !msg.share_declined;
  return (
    <div className={`flex ${isDragon ? "justify-start" : "justify-end"}`} data-testid={`dragon-message-${msg.id}`}>
      <div className={`max-w-[85%] ${isDragon ? "" : "flex flex-col items-end"}`}>
        <div className="flex items-start gap-2">
          {isDragon && <img src={dragonAvatar} alt="Sir Emberbeard" className="w-7 h-7 rounded-full shrink-0 border border-white/10" />}
          <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${isDragon ? "bg-[#2a1f14] border border-[#D4AF37]/20 text-[#f1e0c5]" : "bg-[#3B82F6]/20 text-[#f1e0c5] border border-[#3B82F6]/20"}`}>
            {msg.content}
          </div>
        </div>
        {pendingShare && (
          <div className="mt-1.5 ml-9 flex items-center gap-2 bg-black/20 rounded-lg px-2.5 py-1.5" data-testid={`dragon-share-prompt-${msg.id}`}>
            <span className="text-[10px] text-[#f1e0c5]/50">Share this with your followers?</span>
            <Button size="sm" data-testid={`dragon-share-yes-${msg.id}`} onClick={() => onShareDecision(msg.id, "share")} className="h-6 px-2 text-[10px] rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e]">Yes</Button>
            <Button size="sm" variant="ghost" data-testid={`dragon-share-no-${msg.id}`} onClick={() => onShareDecision(msg.id, "decline")} className="h-6 px-2 text-[10px] rounded-full">No thanks</Button>
          </div>
        )}
        {isDragon && msg.shared && <span className="mt-1 ml-9 text-[10px] text-[#3B82F6] flex items-center gap-1"><Share2 className="w-2.5 h-2.5" /> Shared to Knights Fee</span>}
      </div>
    </div>
  );
}

// Sir Emberbeard, the reformed ancient red dragon companion — a persistent, always-remembers-you
// chat thread + the inbox for his one-way "trigger" messages (signup greeting, raid congrats,
// new Gauntlet tier, a follow notification, character level ups / in-memoriams). See
// dragon_companion.py for his persona + the LLM wiring, dragon_router.py for the endpoints.
export function DragonCompanion() {
  const [open, setOpen] = useState(false);
  const [dragon, setDragon] = useState({ name: "Sir Emberbeard", avatar_url: null });
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  const refreshUnread = () => {
    api.get("/dragon/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
  };

  useEffect(() => {
    refreshUnread();
    const onVisible = () => { if (document.visibilityState === "visible") refreshUnread(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/dragon/messages").then(({ data }) => {
      setDragon(data.dragon);
      setMessages(data.messages);
      setLoaded(true);
    });
    api.post("/dragon/mark-read").then(() => setUnread(0));
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft("");
    setMessages((prev) => [...prev, { id: `temp-${Date.now()}`, role: "user", content: text, shareable: false, shared: false, share_declined: false }]);
    try {
      const { data } = await api.post("/dragon/chat", { content: text });
      setMessages((prev) => [...prev, data]);
    } catch (err) {
      toast.error("Sir Emberbeard didn't hear that — try again?");
    } finally {
      setSending(false);
    }
  };

  const handleShareDecision = async (messageId, decision) => {
    try {
      await api.post(`/dragon/messages/${messageId}/share-decision`, { decision });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, shared: decision === "share", share_declined: decision === "decline" } : m)));
      if (decision === "share") toast.success("Shared to your Knights Fee feed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't do that right now");
    }
  };

  return (
    <>
      <button data-testid="dragon-companion-button" onClick={() => setOpen(true)} className="relative p-2 rounded-full hover:bg-white/5 transition-colors" title="Chat with Sir Emberbeard">
        <Flame className="w-5 h-5 text-[#D90429]" />
        {unread > 0 && (
          <span data-testid="dragon-unread-badge" className="absolute -top-0.5 -right-0.5 bg-[#D90429] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-[#1c1712] border-white/10 text-[#f1e0c5] w-full sm:max-w-md flex flex-col p-0" data-testid="dragon-chat-drawer">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/10 flex-row items-center gap-3">
            {dragon.avatar_url && <img src={dragon.avatar_url} alt={dragon.name} className="w-10 h-10 rounded-full border border-[#D4AF37]/30" />}
            <div>
              <SheetTitle className="font-display text-xl text-left">{dragon.name}</SheetTitle>
              <p className="text-[11px] text-[#f1e0c5]/40 text-left">Ancient (reformed) red dragon</p>
            </div>
          </SheetHeader>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {!loaded ? (
              <p className="text-xs text-[#f1e0c5]/30 text-center py-8">Waking the old dragon…</p>
            ) : messages.length === 0 ? (
              <p className="text-xs text-[#f1e0c5]/30 text-center py-8" data-testid="dragon-no-messages">Say hello — he doesn't bite. Usually.</p>
            ) : (
              messages.map((m) => <DragonMessageBubble key={m.id} msg={m} dragonAvatar={dragon.avatar_url} onShareDecision={handleShareDecision} />)
            )}
          </div>

          <div className="p-4 border-t border-white/10 flex gap-2">
            <Input
              data-testid="dragon-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Say something to Sir Emberbeard…"
              disabled={sending}
              className="bg-black/30 border-white/10"
            />
            <Button data-testid="dragon-chat-send-button" onClick={send} disabled={sending || !draft.trim()} size="icon" className="rounded-full bg-[#D90429] hover:bg-[#b8031f] shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
